import type { ManagedAgentConfigKind } from './api'
import type { AdminUser, Permission, Role } from '@/types'

export const roleLabels: Record<Role, string> = {
  superadmin: '超级管理员',
  content_editor: '内容编辑',
  operator: '运营',
  ai_engineer: 'AI 工程师',
  observer: '客服 / 观察者',
}

export const rolePermissions: Record<Role, Permission[]> = {
  superadmin: [
    'dashboard:read',
    'content:read',
    'content:manage',
    'agent:read',
    'agent:manage',
    'vocabulary:read',
    'operations:read',
    'operations:manage',
    'people:sensitive:read',
    'people:read',
    'people:manage',
    'device:read',
    'device:telemetry:read',
    'device:manage',
    'provider:read',
    'provider:manage',
    'cost:read',
    'budget:manage',
    'health:read',
    'audit:read',
    'ota:read',
    'ota:manage',
    'admin:manage',
  ],
  content_editor: ['dashboard:read', 'content:read', 'content:manage'],
  operator: [
    'dashboard:read',
    'operations:read',
    'operations:manage',
    'people:sensitive:read',
    'people:read',
    'people:manage',
    'device:read',
    'device:telemetry:read',
    'device:manage',
    'ota:read',
    'ota:manage',
    'vocabulary:read',
  ],
  ai_engineer: ['dashboard:read', 'agent:read', 'agent:manage', 'vocabulary:read', 'provider:read', 'health:read'],
  observer: ['dashboard:read', 'device:read'],
}

const rolePriority: Role[] = ['superadmin', 'operator', 'ai_engineer', 'content_editor', 'observer']

export function hasPermission(roles: readonly Role[] | undefined, permission?: Permission): boolean {
  if (!permission) return true
  if (!roles?.length) return false
  return roles.some((role) => rolePermissions[role].includes(permission))
}

export function hasAnyPermission(roles: readonly Role[] | undefined, permissions?: readonly Permission[]): boolean {
  if (!permissions?.length) return true
  return permissions.some((permission) => hasPermission(roles, permission))
}

export function primaryRole(roles: readonly Role[]): Role {
  return rolePriority.find((role) => roles.includes(role)) ?? 'observer'
}

export function roleSummary(roles: readonly Role[]): string {
  if (roles.length === 0) return '未授权'
  return roles.map((role) => roleLabels[role]).join(' · ')
}

/** Managed config 在同一 Agent 页内跨越内容、AI 与成本域，不能只靠页级权限。 */
export function canAccessManagedConfigKind(roles: readonly Role[], kind: ManagedAgentConfigKind): boolean {
  if (roles.includes('superadmin')) return true
  if (kind === 'price_config') return false
  if (kind === 'asr_vocabulary') return roles.some((role) => role === 'ai_engineer' || role === 'operator')
  if (kind === 'knowledge_config') return roles.some((role) => role === 'content_editor' || role === 'ai_engineer')
  return roles.includes('ai_engineer')
}

export function adminDisplayName(user: Pick<AdminUser, 'email' | 'name'>): string {
  if (user.name?.trim()) return user.name.trim()
  return user.email.split('@')[0]?.trim() || '管理员'
}
